import "./404Error.css";
import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SEO from "../../common/context/SeoContext";

// Seconds before the visitor is automatically returned to the homepage. The
// countdown is stated in plain language and can be stopped at any time, so it
// never rushes someone who is reading the page.
const REDIRECT_SECONDS = 15;

const Error = () => {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
  const [autoRedirect, setAutoRedirect] = useState(true);

  // Each render schedules the next tick; at zero we replace history (so Back
  // doesn't return to the missing URL) and send the visitor to the homepage.
  useEffect(() => {
    if (!autoRedirect) return undefined;
    if (secondsLeft <= 0) {
      navigate("/", { replace: true });
      return undefined;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft, autoRedirect, navigate]);

  return (
    <div className="nf-wrap flex min-h-screen flex-col items-center justify-center px-6 py-12 font-sans">
      {/* noindex: the SPA can't return a real 404 status, so tell crawlers
          explicitly this page must not be indexed (avoids soft-404 pollution). */}
      <SEO title="Page not found | JaaGa" noindex />

      <div className="nf-card w-full max-w-xl">
        {/* Letterhead-style accent bar for an official, considered feel. */}
        <div className="nf-accent" />

        <div className="px-8 py-10 text-center sm:px-12 sm:py-12">
          {/* Static seal emblem — a location marker, in keeping with JaaGa's
              property and land-records work. No motion. */}
          <div className="nf-seal">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#153568"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
              <circle cx="12" cy="10" r="2.4" fill="#f0a500" stroke="none" />
            </svg>
          </div>

          <p className="nf-eyebrow">Error 404</p>

          <h1 className="mt-3 text-2xl font-bold tracking-tight text-primary sm:text-[1.75rem]">
            This page could not be found
          </h1>

          {/* Actions */}
          <div className="mt-8 flex w-full flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              onClick={() => navigate("/", { replace: true })}
              className="w-full rounded-lg bg-primary px-6 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-dark sm:w-auto"
            >
              Go to Homepage
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full rounded-lg border border-primary/25 bg-white px-6 py-3 font-semibold text-primary transition-colors hover:bg-secondary sm:w-auto"
            >
              Go Back
            </button>
          </div>

          {/* Redirect notice */}
          <div className="mt-8 border-t border-primary/10 pt-6" aria-live="polite">
            {autoRedirect ? (
              <p className="text-sm text-label">
                You will be redirected to the homepage in{" "}
                <span className="font-semibold text-primary tabular-nums">
                  {secondsLeft} second{secondsLeft === 1 ? "" : "s"}
                </span>
                .{" "}
                <button
                  onClick={() => setAutoRedirect(false)}
                  className="font-semibold text-primary underline underline-offset-4 hover:text-dark"
                >
                  Stay on this page
                </button>
              </p>
            ) : (
              <p className="text-sm text-label">
                Automatic redirect paused. Take your time.
              </p>
            )}
          </div>

          {/* Assisted-support line — this audience often prefers a human. */}
          <p className="mt-5 text-sm text-label">
            Need assistance?{" "}
            <Link
              to="/contactus"
              className="font-semibold text-primary underline underline-offset-4 hover:text-dark"
            >
              Contact our support team
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Error;
